import {
  calculateDynamicRate,
  calculateTotalPayout,
  type MilesProgram,
} from "@/lib/miles-calculations";
import { cn } from "@/lib/utils";
import { TransactionTypes } from "@/stores";
import {
  CalendarDays,
  Clock,
  MessageCircle,
  Send,
  Star,
  Tag,
  Users,
  Zap,
} from "lucide-react";
import { type ReactNode, useState } from "react";
import {
  CARD,
  Disclosure,
  INK,
  MAIN,
  MILES_STEP,
  MUTED,
  OfferHero,
  PAGE,
  TRUSTPILOT_URL,
} from "./OfferReady";
import { money, programDisplayName } from "./offer-format";

const WHATSAPP_NUMBER = "15557039123";
const PHONE_HREF = "tel:+15617860183";
const PHONE_LABEL = "(561) 786-0183";
const BUY = TransactionTypes.BUY;

// Colours from the client's "Buyer quote" design.
const WHATSAPP_BUTTON =
  "flex items-center justify-center rounded-[12px] bg-[#117A43] font-bold text-white no-underline hover:bg-[#0D6537]";

const BENEFITS = [
  {
    icon: Zap,
    title: "Fast, secure transfers",
    body: "Miles in your account as soon as today, in most cases.",
  },
  {
    icon: Tag,
    title: "Below-market rates",
    body: "Bulk pricing, lower than buying miles at market price.",
  },
  {
    icon: Clock,
    title: "Valid for up to 36 months",
    body: "Plenty of time to find the right flight.",
  },
  {
    icon: Send,
    title: "Business and first for less",
    body: "Use your miles for premium cabins and save thousands.",
  },
];

interface BuyerQuoteProps {
  program: MilesProgram;
  /** Shown above the card, e.g. a failed attempt to secure the quote. */
  alert?: ReactNode;
  /** Runs as the WhatsApp chat opens, to pass the quote to an agent. */
  onSecure: () => void;
  /** Left out when the miles are fixed, e.g. a quote staff created. */
  onChangeMiles?: (miles: number) => void;
}

function priceFor(program: MilesProgram, miles: number) {
  const dynamicRate = calculateDynamicRate({
    miles,
    transactionType: BUY,
    sellPrices: program.sellPrices,
    buyPrices: program.buyPrices,
    fallbackRate: program.ratePerMile,
  });
  return {
    dynamicRate,
    ...calculateTotalPayout({
      totalMiles: miles,
      dynamicRate,
      loyaltyStatus: null,
      transactionType: BUY,
      promoCode: program.promoCode,
    }),
  };
}

function shortCount(value: number) {
  return value >= 1_000_000
    ? `${Math.round(value / 10_000) / 100}M`
    : `${Math.round(value / 1000)}k`;
}

function EditMiles({
  program,
  unit,
  onSave,
  onCancel,
}: {
  program: MilesProgram;
  unit: string;
  onSave: (miles: number) => void;
  onCancel: () => void;
}) {
  const [miles, setMiles] = useState(program.sanitizedMiles);
  const min = program.minimumValueForBuy;
  const max = program.maximumValueForBuy;
  const clamp = (value: number) =>
    Math.max(min || 0, max ? Math.min(max, value) : value);
  const stepButton =
    "h-11 flex-[0_0_44px] cursor-pointer rounded-[11px] border border-[#CFD9EC] bg-white text-[22px] leading-none font-bold";

  return (
    <div className="mt-3 rounded-[14px] border border-[#DCE4F5] bg-[#F8FAFF] p-4">
      <label htmlFor="buy-miles" className="mb-2 block text-sm font-bold">
        {unit === "miles" ? "Miles" : "Points"} to buy
      </label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => setMiles((value) => clamp(value - MILES_STEP))}
          aria-label={`Decrease by ${MILES_STEP.toLocaleString()} ${unit}`}
          className={stepButton}
        >
          &minus;
        </button>
        <input
          id="buy-miles"
          type="number"
          inputMode="numeric"
          value={miles}
          min={min || undefined}
          max={max || undefined}
          step={1000}
          onChange={(event) => setMiles(Number(event.target.value) || 0)}
          onBlur={() => setMiles((value) => clamp(value))}
          className="h-11 min-w-0 flex-[1_1_120px] rounded-[11px] border border-[#CFD9EC] bg-white px-3 text-[17px] font-bold"
        />
        <button
          type="button"
          onClick={() => setMiles((value) => clamp(value + MILES_STEP))}
          aria-label={`Increase by ${MILES_STEP.toLocaleString()} ${unit}`}
          className={stepButton}
        >
          +
        </button>
      </div>
      <p className={cn("mt-2 text-[13px]", MUTED)}>
        New total:{" "}
        <strong className={INK}>
          {money(priceFor(program, clamp(miles)).totalPayout)}
        </strong>
        {max > 0 && (
          <>
            {" "}
            &middot; {min.toLocaleString()} – {max.toLocaleString()} {unit}
          </>
        )}
      </p>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => onSave(clamp(miles))}
          className="h-11 flex-auto cursor-pointer rounded-[11px] bg-[#2B51EE] text-[14.5px] font-bold text-white hover:bg-[#1C3BC4]"
        >
          Update quote
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="h-11 flex-none cursor-pointer rounded-[11px] border border-[#CFD9EC] bg-white px-4 text-[14.5px] font-bold text-[#1C3BC4]"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

/** A buy quote as we priced it; the customer secures it with an agent on WhatsApp. */
export const BuyerQuote = ({
  program,
  alert,
  onSecure,
  onChangeMiles,
}: BuyerQuoteProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const miles = program.sanitizedMiles;
  const { dynamicRate, baseAmount, promoCodeDiscount, totalPayout } = priceFor(
    program,
    miles,
  );

  const programName = programDisplayName(program.name);
  const unit = program.category?.toLowerCase().includes("miles")
    ? "miles"
    : "points";
  const perUnit = unit === "miles" ? "mile" : "point";
  const centsPerUnit = `${(dynamicRate * 100).toFixed(2)}¢`;
  const total = money(totalPayout);

  const message = `Hi, I would like to go ahead with my quote: ${miles.toLocaleString("en-US")} ${programName} ${unit} for ${total}.`;
  const whatsappHref = `https://api.whatsapp.com/send?phone=${WHATSAPP_NUMBER}&text=${encodeURIComponent(message)}`;
  const whatsappLink = {
    href: whatsappHref,
    target: "_blank",
    rel: "noopener noreferrer",
    onClick: onSecure,
  };
  const calcRow = "flex justify-between gap-3 border-b border-[#E6EBF5] py-2";

  return (
    <div className={PAGE}>
      <OfferHero
        title={`Your ${unit} are ready to buy`}
        steps={["Request", "Quote", "Agent"]}
      />

      <main className={MAIN}>
        {alert}
        <section className={CARD}>
          <p
            className={cn(
              "text-[12.5px] font-bold tracking-[0.09em] uppercase",
              MUTED,
            )}
          >
            Total price
          </p>
          <p className="mt-1 mb-1.5 text-[40px] leading-[1.05] font-extrabold tracking-[-0.03em] min-[821px]:text-[50px]">
            {total}
          </p>
          <p className={cn("mb-1.5 text-sm leading-normal", MUTED)}>
            Transferred to your {programName} account as soon as today, in most
            cases.
          </p>

          <div className="mt-2.5 flex flex-wrap items-center justify-between gap-x-3.5 gap-y-2 border-y border-[#EEF1F7] py-[9px]">
            <p className="text-[14.5px] font-semibold">
              {miles.toLocaleString()} {programName} {unit} &middot;{" "}
              <span className={cn("font-medium", MUTED)}>
                {centsPerUnit} per {perUnit}
              </span>
            </p>
            {onChangeMiles && !isEditing && (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="h-[34px] cursor-pointer rounded-full bg-[#EEF2FE] px-[13px] text-[13px] font-bold text-[#1C3BC4]"
              >
                Change
              </button>
            )}
          </div>

          {onChangeMiles && isEditing && (
            <EditMiles
              program={program}
              unit={unit}
              onSave={(value) => {
                onChangeMiles(value);
                setIsEditing(false);
              }}
              onCancel={() => setIsEditing(false)}
            />
          )}

          <div
            className={cn(
              "mt-3 flex flex-wrap items-center gap-x-[18px] gap-y-2.5 text-[13.5px]",
              MUTED,
            )}
          >
            <span className="inline-flex items-center gap-[7px]">
              <Users
                className="h-4 w-4 text-[#1C3BC4]"
                strokeWidth={2.2}
                aria-hidden
              />
              <span>
                <strong className={cn("font-extrabold", INK)}>50,000+</strong>{" "}
                customers served
              </span>
            </span>
            <span className="inline-flex items-center gap-[7px]">
              <CalendarDays
                className="h-4 w-4 text-[#1C3BC4]"
                strokeWidth={2.2}
                aria-hidden
              />
              <span>
                In business since{" "}
                <strong className={cn("font-extrabold", INK)}>2015</strong>
              </span>
            </span>
          </div>

          <a
            {...whatsappLink}
            className={cn(
              WHATSAPP_BUTTON,
              "mt-3.5 min-h-[52px] gap-2.5 px-5 py-[15px] text-base min-[821px]:text-[17px]",
            )}
          >
            <MessageCircle className="h-5 w-5" strokeWidth={2.1} aria-hidden />
            Secure your {unit} on WhatsApp
          </a>
          <p className={cn("mt-2 text-center text-[13px]", MUTED)}>
            Opens WhatsApp with your quote filled in. Your agent confirms
            payment and transfer.
          </p>

          <div
            className={cn(
              "mt-3 flex items-center justify-center gap-2 text-[13.5px]",
              MUTED,
            )}
          >
            <span className="inline-flex gap-px" aria-hidden>
              {Array.from({ length: 5 }, (_, index) => (
                <Star
                  key={index}
                  className="h-3.5 w-3.5 fill-[#00B67A] text-[#00B67A]"
                  strokeWidth={0}
                />
              ))}
            </span>
            <span>
              <strong className={cn("font-bold", INK)}>4.7</strong> &middot;{" "}
              <a
                href={TRUSTPILOT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#51607E] underline hover:text-[#1C3BC4]"
              >
                1,250+ reviews on Trustpilot
              </a>
            </span>
          </div>

          <p
            className={cn(
              "mt-2.5 text-center text-[13.5px] leading-normal",
              MUTED,
            )}
          >
            Prefer email? Reply to your quote email. Or call{" "}
            <a
              href={PHONE_HREF}
              className="font-bold whitespace-nowrap text-[#1C3BC4] underline"
            >
              {PHONE_LABEL}
            </a>
          </p>

          <div className="mt-2.5">
            <Disclosure
              title="How your price is calculated"
              className="text-[#1C3BC4]"
            >
              <div className="rounded-[12px] border border-[#DCE4F5] bg-[#F8FAFF] px-3.5 py-1.5 text-sm">
                <div className={calcRow}>
                  <span className={MUTED}>
                    {miles.toLocaleString()} {unit} &times; {centsPerUnit}
                  </span>
                  <span className="font-semibold">{money(baseAmount)}</span>
                </div>
                {promoCodeDiscount !== 0 && (
                  <div className={calcRow}>
                    <span className={MUTED}>
                      Promo code {program.promoCode?.name}
                    </span>
                    <span className="font-semibold text-[#1C3BC4]">
                      {money(promoCodeDiscount)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between gap-3 pt-[9px] pb-1.5 text-[15px] font-extrabold">
                  <span>Your total</span>
                  <span>{total}</span>
                </div>
              </div>
            </Disclosure>
          </div>
        </section>

        <section className={cn(CARD, "shadow-none min-[821px]:py-5")}>
          <h2 className="mb-3 text-[17px] font-extrabold tracking-[-0.01em]">
            Why buy {unit} with us
          </h2>
          <div className="grid grid-cols-1 gap-x-5 gap-y-3.5 min-[821px]:grid-cols-2">
            {BENEFITS.map(({ icon: Icon, title, body }) => (
              <div key={title} className="flex items-start gap-[11px]">
                <span className="flex h-[34px] flex-[0_0_34px] items-center justify-center rounded-[10px] bg-[#EEF2FE]">
                  <Icon
                    className="h-[18px] w-[18px] text-[#1C3BC4]"
                    strokeWidth={2}
                    aria-hidden
                  />
                </span>
                <div>
                  <p className="text-[14.5px] leading-[1.3] font-bold">
                    {title}
                  </p>
                  <p
                    className={cn("mt-0.5 text-[13.5px] leading-[1.45]", MUTED)}
                  >
                    {body}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 border-t border-[#EEF1F7] pt-3.5 text-sm leading-normal">
            Buying a larger amount or need custom pricing?{" "}
            <a {...whatsappLink} className="font-bold text-[#0B5E33] underline">
              Ask an agent on WhatsApp
            </a>
          </p>
        </section>
      </main>

      <div className="sticky bottom-0 z-40 flex items-center justify-between gap-3 border-t border-[#E3E8F2] bg-white px-4 pt-2.5 pb-[calc(10px+env(safe-area-inset-bottom))] shadow-[0_-6px_20px_rgba(12,27,58,0.1)] min-[821px]:hidden">
        <div className="min-w-0">
          <p
            className={cn(
              "truncate text-[11px] font-bold tracking-[0.06em] uppercase",
              MUTED,
            )}
          >
            Your quote
          </p>
          <p className="text-[21px] leading-[1.1] font-extrabold tracking-[-0.02em]">
            {total}
          </p>
          <p className={cn("mt-0.5 truncate text-[11.5px]", MUTED)}>
            {shortCount(miles)} {programName} {unit}
          </p>
        </div>
        <a
          {...whatsappLink}
          className={cn(
            WHATSAPP_BUTTON,
            "h-[46px] flex-none gap-[7px] px-4 text-[14.5px] whitespace-nowrap",
          )}
        >
          <MessageCircle
            className="h-[17px] w-[17px]"
            strokeWidth={2.1}
            aria-hidden
          />
          Secure on WhatsApp
        </a>
      </div>
    </div>
  );
};

/** Holds the buy quote's shape while it loads, so nothing jumps. */
export const BuyerQuoteSkeleton = () => {
  const bone = "animate-pulse rounded-md bg-[#EEF1F7]";
  return (
    <div className={PAGE} aria-busy="true" aria-label="Loading your quote">
      <OfferHero
        title="Your miles are ready to buy"
        steps={["Request", "Quote", "Agent"]}
      />
      <main className={MAIN}>
        <section className={CARD}>
          <div className={cn(bone, "h-4 w-24")} />
          <div className={cn(bone, "my-2 h-10 w-48 min-[821px]:h-[50px]")} />
          <div className={cn(bone, "h-4 w-full max-w-[420px]")} />
          <div className="mt-3 flex items-center justify-between border-y border-[#EEF1F7] py-[9px]">
            <div className={cn(bone, "h-5 w-64")} />
            <div className={cn(bone, "h-[34px] w-[78px] rounded-full")} />
          </div>
          <div className="mt-3 flex flex-wrap gap-x-[18px] gap-y-2.5">
            <div className={cn(bone, "h-5 w-48")} />
            <div className={cn(bone, "h-5 w-44")} />
          </div>
          <div
            className={cn(
              bone,
              "mt-3.5 h-[52px] w-full rounded-[12px] bg-[#DCE4F5]",
            )}
          />
          <div className={cn(bone, "mx-auto mt-3 h-4 w-72 max-w-full")} />
          <div className={cn(bone, "mx-auto mt-3 h-4 w-56")} />
          <div className={cn(bone, "mx-auto mt-3 h-4 w-64 max-w-full")} />
          <div className={cn(bone, "mt-5 h-4 w-48")} />
        </section>
        <section className={cn(CARD, "shadow-none min-[821px]:py-5")}>
          <div className={cn(bone, "mb-4 h-5 w-44")} />
          <div className="grid grid-cols-1 gap-x-5 gap-y-3.5 min-[821px]:grid-cols-2">
            {Array.from({ length: 4 }, (_, index) => (
              <div key={index} className="flex gap-[11px]">
                <div className={cn(bone, "h-[34px] w-[34px] rounded-[10px]")} />
                <div className="flex-1">
                  <div className={cn(bone, "h-4 w-40")} />
                  <div className={cn(bone, "mt-1.5 h-4 w-full")} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};
